import { useQuery } from '@tanstack/react-query';
import { getUpcomingInvoices } from 'src/requests/invoices';
import { getCurrentSubscriptions } from 'src/requests/subscriptions';
import { PaymentInterval, SubscriptionItem } from 'src/types/payment';
import moment from 'moment';

export interface BillingAlertData {
  daysUntilDue: number;
  isOverdue: boolean;
  dueDate: string;
  amount: number;
  currency: string;
  interval: PaymentInterval;
}

export const useBillingAlert = (orgId: string | undefined) => {
  return useQuery({
    queryKey: ['billingAlert', orgId],
    queryFn: async () => {
      if (!orgId) return null;

      const [invoiceResult, subscriptions] = await Promise.all([
        getUpcomingInvoices(orgId),
        getCurrentSubscriptions(orgId),
      ]);

      if (invoiceResult.error || !invoiceResult.data) return null;
      if (!subscriptions || subscriptions.length === 0) return null;

      return {
        invoice: invoiceResult.data,
        subscription: subscriptions[0],
      };
    },
    select: (data): BillingAlertData | null => {
      if (!data) return null;
      const { invoice, subscription } = data;

      const mainSubscription = subscription.items.find((item: SubscriptionItem) =>
        ['coach', 'therapist'].includes(item.product.productType)
      );

      if (!mainSubscription) return null;

      const periodEndVal =
        invoice.periodEnd.toString().length === 10 ? invoice.periodEnd * 1000 : invoice.periodEnd;

      const dueDate = moment(periodEndVal);
      const today = moment();

      const daysUntilDue = dueDate.clone().startOf('day').diff(today.clone().startOf('day'), 'days');

      const interval = mainSubscription.product.price.interval;
      const threshold = interval === 'month' ? 7 : 30;

      if (daysUntilDue > threshold) {
        return null;
      }

      return {
        daysUntilDue,
        isOverdue: daysUntilDue < 0,
        dueDate: dueDate.format(),
        amount: invoice.amountDue,
        currency: invoice.currency,
        interval,
      };
    },
    enabled: !!orgId,
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });
};
